import React, { useCallback, useEffect, useState } from "react";

import { ListCardInfo } from "./components/cards";
import { LineChart } from "./components/charts";
import { ListData } from "./components/listData";

import { MyMotor } from "./components/motor";
import { Notifications } from "./components/notifications";

import { useGetBookingStat } from "./hooks/useGetBookingStat";
import { useGetMyMotor } from "./hooks/useGetMyMotor";
import { useGetNotificationsWitbooking } from "./hooks/useGetNotificationsWitbooking";
import { useGetReservations } from "./hooks/useGetReservations";
import { useGetDashboard } from "./hooks/useGetDashboard";

import { BookingFilterCriteria, FilterDashboard } from "./types/types";

import {
  DASHBOARD_FILTER_CRITERIA,
  DASHBOARD_FILTER_CRITERIA_DATE,
  LOCAL_STORAGE_KEYS,
} from "../witadmin/Common/constants";
import { ModalTailwindCustomDate } from "./components/modals";
import { FullWidthLoader } from "../../components/full-width-loader";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useProperty } from "../../components/property-provider";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";
import { useHistory } from "react-router";
import { staticURL } from "../../layout/classic/menu";
import {useTheme} from "styled-components";
import { WelcomeModal } from "./components/welcomeModal";

function getStartAndEndDate(dates: string | undefined) {
  const days = dates?.split(",");
  if (days?.length) {
    const lastDay = days[0].split("-");
    if (lastDay?.length === 2) {
      return {
        startDate: dayjs(lastDay[0].trim()).format("YYYY-MM-DD"),
        endDate: dayjs(lastDay[1].trim()).format("YYYY-MM-DD"),
      };
    }
  }
  return {};
}

const customFilterValue = "0";

interface DashboardProps {
  propertyTicker: string;
}

const FilterCriteriaDate: FilterDashboard[] = [
  {
    value: "7",
    labelName: DASHBOARD_FILTER_CRITERIA_DATE.LAST_N_DAYS,
    labelValue: { days: 7 },
  },
  {
    value: "15",
    labelName: DASHBOARD_FILTER_CRITERIA_DATE.LAST_N_DAYS,
    labelValue: { days: 15 },
  },
  {
    value: "30",
    labelName: DASHBOARD_FILTER_CRITERIA_DATE.LAST_N_DAYS,
    labelValue: { days: 30 },
  },
  {
    value: customFilterValue,
    labelName: DASHBOARD_FILTER_CRITERIA_DATE.CUSTOM_DATE,
  },
];

const FilterCriteria: FilterDashboard[] = [
  {
    value: `${BookingFilterCriteria.BY_BOOKING_DATE}`,
    labelName: DASHBOARD_FILTER_CRITERIA.BY_BOOKING_DATE,
  },
  {
    value: `${BookingFilterCriteria.BY_BOOKING_CHECKOUT_DATE}`,
    labelName: DASHBOARD_FILTER_CRITERIA.BY_BOOKING_CHECKOUT_DATE,
  },
  {
    value: `${BookingFilterCriteria.BY_CHECKIN_DATE}`,
    labelName: DASHBOARD_FILTER_CRITERIA.BY_CHECKIN_DATE,
  },
  {
    value: `${BookingFilterCriteria.BY_CHECKOUT_DATE}`,
    labelName: DASHBOARD_FILTER_CRITERIA.BY_CHECKOUT_DATE,
  },
];
const welcomeDashboardModalCookie = Cookies.get("welcomeDashboardModalSepteo");

const initialFilterCriteria = () => {
  const DASHBOARD_FILTER_RESERVATION = window.sessionStorage.getItem(
    LOCAL_STORAGE_KEYS.DASHBOARD_FILTER_RESERVATION
  );
  if (DASHBOARD_FILTER_RESERVATION) {
    const indexFCD = FilterCriteria.findIndex(
      (fcd: FilterDashboard) => fcd.labelName === DASHBOARD_FILTER_RESERVATION
    );
    if (indexFCD !== -1) {
      return FilterCriteria[indexFCD];
    }
  }
  return FilterCriteria[0];
};

const initialFilterCriteriaDate = () => {
  const filterDays = window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.DASHBOARD_FILTER_DAYS);
  const customSearch =
    window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.DASHBOARD_CUSTOM_SEARCH) || "";
  if (filterDays) {
    const indexFCD = FilterCriteriaDate.findIndex(
      (fcd: FilterDashboard) => fcd.value === filterDays
    );
    const isCustomFilterAndHasSelectedDatesSaved =
      FilterCriteriaDate[indexFCD].value === customFilterValue &&
      !!getStartAndEndDate(customSearch);
    if (
      FilterCriteriaDate[indexFCD].value !== customFilterValue ||
      isCustomFilterAndHasSelectedDatesSaved
    ) {
      return FilterCriteriaDate[indexFCD];
    }
  }
  return FilterCriteriaDate[0];
};

const initialDateByFilterDays = () => {
  const filterDays = window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.DASHBOARD_FILTER_DAYS);
  const customSearch =
    window.sessionStorage.getItem(LOCAL_STORAGE_KEYS.DASHBOARD_CUSTOM_SEARCH) || "";
  let currentCriteria: FilterDashboard;
  const isFilterANumber = filterDays && !Number.isNaN(Number(filterDays));
  if (isFilterANumber) {
    const indexFCD = FilterCriteriaDate.findIndex(
      (fcd: FilterDashboard) => fcd.value === filterDays
    );
    currentCriteria = FilterCriteriaDate[indexFCD];
  } else {
    currentCriteria = FilterCriteriaDate[0];
  }
  if (filterDays === customFilterValue) {
    const { startDate, endDate } = getStartAndEndDate(customSearch);
    if (startDate && endDate) {
      return { startDate, endDate };
    }
    currentCriteria = FilterCriteriaDate[0];
  }
  return {
    startDate: dayjs().subtract(Number(currentCriteria.value), "d").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  };
};

const Dashboard = ({ propertyTicker }: DashboardProps) => {
  const history = useHistory();
  const [hideWelcomeModal, setHideWelcomeModal] = useState(!!welcomeDashboardModalCookie);
  const { data: dataBookingStat, loading: loadingBookingStat } = useGetBookingStat({
    propertyTicker,
  });
  const { data: dataEngineStat, loading: loadingEngineStat } = useGetMyMotor({ propertyTicker });
  const {
    data: dataNotificationsWitbooking,
    loading: loadingNotificationsWitbooking,
  } = useGetNotificationsWitbooking({ propertyTicker });
  const { data: dataListReservations, loading: loadingListReservations } = useGetReservations({
    propertyTicker,
  });
  const { info } = useProperty();
  const [actualFilterCriteria, setActualFilterCriteria] = useState<FilterDashboard>(
    initialFilterCriteria
  );
  const [actualFilterCriteriaDate, setActualFilterCriteriaDate] = useState<FilterDashboard>(
    initialFilterCriteriaDate
  );
  const [customDate, setCustomDate] = useState(initialDateByFilterDays);
  const [openModal, setOpenModal] = useState(false);
  const { data: dataDashboard, loading: loadingDashboard } = useGetDashboard({
    criteria: actualFilterCriteria,
    start: customDate.startDate,
    end: customDate.endDate,
    propertyTicker,
  });
  const chainData: IPropertyChainData | undefined | null = useChainData();
const theme = useTheme();

  const handleChangeFirstFilter = useCallback((v: FilterDashboard) => {
    setActualFilterCriteria(v);
    window.sessionStorage.setItem(LOCAL_STORAGE_KEYS.DASHBOARD_FILTER_RESERVATION, v.labelName);
  }, []);

  const handleChangeSecondFilter = useCallback((v: FilterDashboard) => {
    if (v.value === customFilterValue) {
      setOpenModal(true);
    } else {
      setCustomDate({
        startDate: dayjs().subtract(Number(v.value), "d").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
      });
    }
    window.sessionStorage.setItem(LOCAL_STORAGE_KEYS.DASHBOARD_FILTER_DAYS, v.value);
    setActualFilterCriteriaDate(v);
  }, []);

  const handleCloseWelcomeModal = useCallback(() => {
    Cookies.set("welcomeDashboardModalSepteo", "hide");
    setHideWelcomeModal(true);
  }, []);

  const handleChangeDate = useCallback(
    ({
      e,
      startDate,
      endDate,
    }: {
      e: React.MouseEvent<HTMLSpanElement, MouseEvent>;
      startDate: string;
      endDate: string;
    }) => {
      e.preventDefault();
      setCustomDate({ startDate, endDate });
    },
    []
  );

  const closeDateModal = useCallback(() => {
    setOpenModal(prevState => !prevState);
  }, []);

  useEffect(() => {
    if (chainData?.isChain) {
      window.location.replace(`${staticURL}/${propertyTicker}/reservations`);
    }
  }, [history, chainData, propertyTicker]);

  if (!chainData) {
    return <FullWidthLoader />;
  }

  return (
    <div className={`px-3 p-4 h-full mb-4 text-[${theme.textColor}]`}>
      {openModal && (
        <ModalTailwindCustomDate
          onHandleSearch={handleChangeDate}
          handleClose={closeDateModal}
          currentStartDate={customDate.startDate}
          currentEndDate={customDate.endDate}
        />
      )}
      {!hideWelcomeModal && <WelcomeModal onCloseModal={handleCloseWelcomeModal} />}
      <div>
        {!loadingBookingStat ? (
          <ListCardInfo data={dataBookingStat} />
        ) : (
          <span>
            <FullWidthLoader />
          </span>
        )}
      </div>
      <div className="flex flex-nowrap mt-6 2xl:h-[625px] h-[560px]">
        <div className="w-1/2 max-w-1/2 flex flex-col pr-2.5 gap-5">
          <div className="h-[370px]">
            {!loadingDashboard ? (
              <LineChart
                current={dataDashboard.currentYear || []}
                past={dataDashboard.pastYear || []}
                dataFirstFilter={FilterCriteria}
                valueFirstFilter={actualFilterCriteria}
                handleChangeFirstFilter={handleChangeFirstFilter}
                dataSecondFilter={FilterCriteriaDate}
                valueSecondFilter={actualFilterCriteriaDate}
                handleChangeSecondFilter={handleChangeSecondFilter}
                defaultCurrency={info.defaultCurrency}
              />
            ) : (
              <span>
                <FullWidthLoader />
              </span>
            )}
          </div>
          <div className="h-[170px] max-h-[170px] h-[235px] 2xl:max-h-[235px]">
            {!loadingListReservations ? (
              <ListData data={dataListReservations} />
            ) : (
              <span>
                <FullWidthLoader />
              </span>
            )}
          </div>
        </div>
        <div className="w-1/4 pl-2.5 pr-1.5 max-h-full h-full">
          {!loadingEngineStat ? (
            <MyMotor bestSelling={dataEngineStat?.bestSelling} myMotor={dataEngineStat?.myMotor} />
          ) : (
            <span>
              <FullWidthLoader />
            </span>
          )}
        </div>
        <div className="w-1/4 pl-3 max-h-full h-full">
          {!loadingNotificationsWitbooking ? (
            <Notifications data={dataNotificationsWitbooking} />
          ) : (
            <span>
              <FullWidthLoader />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default withAuthorizationVerifier(Dashboard);
